import { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Dialog } from '@efira/ui';
import type { ReactNode } from 'react';

/**
 * Место под закреплённый подвал окна.
 *
 * Пусто, пока содержимое окна не положит туда своё главное действие
 * (`ModalFooter`). Контекст, а не свойство рамы: кнопка «Оформить Pro»
 * живёт состоянием окна тарифа - выбранным периодом и ходом оплаты, - и
 * поднимать это состояние в маршрут ради раскладки значило бы разложить
 * одно окно по двум файлам.
 */
const FooterSlot = createContext<HTMLElement | null>(null);

/**
 * ГЛАВНОЕ ДЕЙСТВИЕ ОКНА, КОТОРОЕ НЕ УЕЗЖАЕТ ПОД КРОМКУ.
 *
 * Находка аудита единицы 16 (09): на экране 900 px кнопка, ради которой
 * окно открыли, оказывалась ниже видимой области. Что попало сюда -
 * стоит в подвале окна и не прокручивается вместе с телом.
 */
export function ModalFooter({ children }: { children: ReactNode }) {
  const slot = useContext(FooterSlot);

  return slot === null ? null : createPortal(children, slot);
}

/**
 * Окно модального слоя консоли.
 *
 * СЛОЙ, А НЕ НАБОР ОКОН ПО МЕСТУ. У эталона настройки, аналитика, история,
 * партнёрская программа, смена игры и тариф - модальные окна поверх консоли,
 * у каждого свой адрес, каждое открывается по прямой ссылке, и консоль под
 * ним не перезагружается и не теряет состояние
 * (docs/reference/LIVECADE_UX.md, раздел 7).
 *
 * КАК ЭТО ДЕРЖИТСЯ. Окна - дочерние маршруты маршрута консоли, а сама консоль
 * отрисована в родителе. React Router при переходе на дочерний маршрут
 * родителя не размонтирует: состояние консоли - выбранная игра, ширины
 * колонок, прокрутка лент - переживает открытие и закрытие окна само собой, а
 * не потому, что его кто-то сохранил.
 *
 * ЗАКРЫТИЕ ВОЗВРАЩАЕТ АДРЕС. Окно не «прячется», а уходит из адреса: иначе
 * кнопка «назад» браузера и крестик означали бы разное.
 */
export interface ConsoleModalProps {
  title: string;
  children: ReactNode;
  /** Своя шапка: у окон консоли там вкладки и фильтры. */
  header?: ReactNode;
  dialogClassName?: string;
  /**
   * Короткое имя окна в словаре реестра: `switcher`, `plan`, `analytics`.
   *
   * По нему крестик получает пометку `data-<имя>="close"` - пункт реестра
   * про закрытие окна становится назван сценарием поимённо, а не «через
   * зону» (ворота честности реестра).
   */
  windowKey?: string;
  /** Классы `modal-content`: высота окна и её поведение. */
  contentClassName?: string;
  /** Куда возвращает закрытие. По умолчанию - корень консоли. */
  closeTo?: string;
  /** Пометки на заголовке окна - когда заголовок в окне один. */
  titleData?: Record<string, string>;
  /** Видимый заголовок, если он уточняет рубрику окна. */
  heading?: string;
}

export function ConsoleModal({
  title,
  children,
  header,
  dialogClassName,
  contentClassName,
  windowKey,
  closeTo = '/app',
  titleData,
  heading,
}: ConsoleModalProps) {
  const navigate = useNavigate();
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  return (
    <Dialog
      open
      onClose={() => {
        void navigate(closeTo);
      }}
      title={title}
      header={header}
      {...(titleData === undefined ? {} : { titleData })}
      {...(heading === undefined ? {} : { heading })}
      {...(windowKey === undefined ? {} : { closeData: { [`data-${windowKey}`]: 'close' } })}
      /*
       * ВЫСОТА ОКНА - ПО СОДЕРЖИМОМУ, И ЭТО СВОЙСТВО КОМПОНЕНТА.
       *
       * Находка владельца: окно истории растянуто на весь экран под одной
       * строкой пустого состояния. Высоту «по содержимому» сделали в
       * аналитике и не сделали в истории - правка по месту вместо правила,
       * и следующее окно повторило бы ошибку.
       *
       * ПОТОЛОК ЗАДАН НЕ ЗДЕСЬ, А В ПРИМИТИВЕ ОКНА (единица 12): высота
       * экрана минус полоса тостов (`--toast-lane`). Прежние 85 % стояли
       * здесь и не были ничем обоснованы; новое число выведено из высоты
       * тоста, и из него же следует, что тост не ложится на окно. Одно
       * место на все окна продукта - и на `Modal` тоже.
       * Проверяется воротами `e2e/console-modals.spec.ts`.
       */
      {...(dialogClassName === undefined ? {} : { dialogClassName })}
      {...(contentClassName === undefined ? {} : { contentClassName })}
    >
      {/*
        ТЕЛО ОКНА - СВОИМИ ОТСТУПАМИ, А НЕ КЛАССОМ БИБЛИОТЕКИ.
        Класс `modal-body` FlyonUI рассчитан на её же раму и внутри окна
        shadcn давал несимметричные поля: слева 16 px, справа -11 - ворота
        окон поймали это первым прогоном. Отступ один со всех сторон, и он
        наш.
      */}
      {/*
        ПРОДОЛЖЕНИЕ ВИДНО - `scroll-fade-b` ПРЕСЕТА.

        Находка аудита единицы 16 (09): у окон тарифа и партнёрской
        программы содержимое уходило под нижнюю кромку без единого признака,
        что там что-то есть, - главное действие оказывалось за экраном.

        Утилита пресета гасит нижнюю кромку, пока прокрутка не дошла до
        конца, и убирает затухание в самом низу (`animation-timeline:
        scroll(self y)`). Своего кода нет, правило одно на все двенадцать
        окон.
      */}
      <div
        className="min-h-0 flex-1 overflow-y-auto p-4 scroll-fade-b"
        data-console-modal={title}
        data-modal-body
      >
        <FooterSlot value={slot}>{children}</FooterSlot>
      </div>

      {/*
        Подвал ПУСТ, пока в него ничего не положили, и тогда его не видно:
        `:empty` перестаёт совпадать, как только портал вставит в него узел.
        Признака «есть ли подвал» в свойствах рамы нет - его несёт сам DOM.
      */}
      <div
        data-modal-footer
        ref={setSlot}
        className="border-foreground/10 bg-popover flex shrink-0 flex-wrap items-center justify-end gap-2 border-t p-4 empty:hidden"
      />
    </Dialog>
  );
}
